import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import 'moment-timezone';
import { getParams } from '../../common/common.func';
import { PagedList } from '../../common/common.types';
import { AzcMoment } from '../../common/datetime/azc-moment.const';
import { ActivityFilter, ActivityPaymentStatus, ActivityStatus, ActivitySummary, NewActivity, UpdatedActivity } from './activity.types';

@Injectable()
export class ActivityService {
	private activitiesUrl = 'api/activities';
	private eventActivitiesUrl = (eventId: number) => `api/events/${eventId}/activities`;
	private activityUrl = (eventId: number, activityId: number) =>
		`${this.eventActivitiesUrl(eventId)}/${activityId}`
	private activityNotesUrl = (eventId: number, activityId: number) => `${this.activityUrl(eventId, activityId)}/notes`;
	private activityStatusUrl = (eventId: number, activityId: number, status: ActivityStatus) =>
		`${this.activityUrl(eventId, activityId)}/status/${status}`
	private activityPaymentStatusUrl = (eventId: number, activityId: number, status: ActivityPaymentStatus) =>
		`${this.activityUrl(eventId, activityId)}/paymentStatus/${status}`

	constructor(private _http: HttpClient) { }

	getActivities(filter?: ActivityFilter): Promise<PagedList<ActivitySummary>> {
		const actualFilter: ActivityFilter = {
			...filter
		};
		actualFilter.activityBefore = AzcMoment.formatEventDate(actualFilter.activityBefore);
		actualFilter.activityAfter = AzcMoment.formatEventDate(actualFilter.activityAfter);
		const options = { params: getParams(actualFilter) };

		return this._http.get<PagedList<ActivitySummary>>(this.activitiesUrl, options).toPromise();
	}

	addActivity(eventId: number, newActivity: NewActivity): Promise<number> {
		const activity = {
			...newActivity,
			startsOn: AzcMoment.formatEventDate(newActivity.startsOn),
			endsOn: AzcMoment.formatEventDate(newActivity.endsOn),
			timezone: newActivity.timezone || moment.tz.guess()
		};

		return this._http.post<number>(this.eventActivitiesUrl(eventId), activity).toPromise();
	}

	updateActivity(eventId: number, activityId: number, updatedActivity: UpdatedActivity): Promise<void> {
		const activity = {
			...updatedActivity,
			startsOn: AzcMoment.formatEventDate(updatedActivity.startsOn),
			endsOn: AzcMoment.formatEventDate(updatedActivity.endsOn),
			timezone: updatedActivity.timezone || moment.tz.guess()
		};
		return this._http.put<void>(this.activityUrl(eventId, activityId), activity).toPromise();
	}

	updateActivityNotes(eventId: number, activityId: number, notes: string): Promise<void> {
		const options = {
			headers: {
				'Content-Type': 'application/json'
			}
		};
		return this._http.put<void>(this.activityNotesUrl(eventId, activityId), JSON.stringify(notes), options).toPromise();
	}

	updateActivityStatus(eventId: number, activityId: number, status: ActivityStatus, notes: string): Promise<void> {
		const options = {
			headers: {
				'Content-Type': 'application/json'
			}
		};
		return this._http.put<void>(this.activityStatusUrl(eventId, activityId, status), JSON.stringify(notes), options).toPromise();
	}

	updateActivityPaymentStatus(eventId: number, activityId: number, status: ActivityPaymentStatus, totalCost?: number, notes?: string): Promise<void> {
		return this._http.put<void>(this.activityPaymentStatusUrl(eventId, activityId, status), { totalCost: totalCost, notes: notes }).toPromise();
	}

	deleteActivity(eventId: number, activityId: number): Promise<void> {
		return this._http.delete<void>(this.activityUrl(eventId, activityId)).toPromise();
	}

	previewBookingRequest(eventId: number, activityId: number, conciergeNotes: string): Promise<string> {
		const options = {
			params: { conciergeNotes },
			responseType: 'text' as 'text'
		};

		return this._http.get(`${this.activityUrl(eventId, activityId)}/emails/bookingRequest`, options).toPromise();
	}
}
